import * as moment from 'moment';
import { useEffect, useState, FormEvent } from 'react';
import { IContact, IListItem, IMicrositeRecipientForList } from '@nkr/models/index';
import { DonorOrigin, MicrositeEventType, ReasonType, transplantEventTypes } from '@nkr/enums/index';
import { IChangeStatusRequest } from '@nkr/store/index';
import { ViewWrapper } from '@nkr/views/donors/watr/styles';
import { Alert, Button, Checkbox, Col, ControlLabel, FormControl, FormGroup, Glyphicon, HelpBlock, Radio, Row } from 'react-bootstrap';
import DashRegistrationTypeahead from './dash-registration-typeahead';
import NkrRecipientTypeahead from './nkr-recipient-typeahead';
import StatusConfirmation, { confirmationTypes } from './status-confirmation';
import BulkStatusConfirmation from './bulk-status-confirmation';
import CenterTypeahead from './center-typeahead';
import Drawer from 'rc-drawer';
import EventHistoryAccordion from './event-history-accordion';
import CoordinatorPicker from './coordinator-picker';
import EventTypePicker from './event-type-picker';
import { NumberSequence } from 'ag-grid-community';

interface IProps {
    open: boolean,
    missingData: boolean,
    recipient?: IMicrositeRecipientForList,
    recipients?: IMicrositeRecipientForList[],
    eventTypeList: IListItem[],
    coordinatorList: IContact[],
    onClosed: () => void,
    onStatusChanged: (recipient: IMicrositeRecipientForList, request: IChangeStatusRequest) => void,
    onBulkStatusChanged?: (request: IChangeStatusRequest) => void,
}

const Req = () => {
    return (<span style={{ color: 'darkred' }}>*</span>);
}

export default function ChangeStatus({ open, missingData, recipient, recipients, eventTypeList, coordinatorList, onClosed, onStatusChanged, onBulkStatusChanged }: IProps) {
    const isBulk = recipients != null;

    const [eventId, setEventId] = useState<number>(-1);
    const [eventType, setEventType] = useState<number>(-1);
    const [reason, setReason] = useState<number>(isBulk ? ReasonType.Other : -1);
    const [transplantDate, setTransplantDate] = useState<string>('');
    const [isNkrTansplant, setIsNkrTansplant] = useState<boolean>(true);
    const [nkrRecipientId, setNkrRecipientId] = useState<number>(undefined);
    const [hasMedicare, setHasMedicare] = useState<boolean>(undefined);
    const [donorOriginType, setDonorOriginType] = useState<number>(-1);
    const [donorOriginOther, setDonorOriginOther] = useState<string>('');
    const [isDonorFound, setIsDonorFound] = useState<boolean>(true);
    const [dashRegistrationId, setDashRegistrationId] = useState<number>(undefined);
    const [comment, setComment] = useState<string>('');
    const [newCenterId, setNewCenterId] = useState<number>(undefined);
    const [newCoordinatorId, setNewCoordinatorId] = useState<number>(-1);
    const [isNkrCenter, setIsNkrCenter] = useState<boolean>(true);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [isTxpEdit, setTxpEdit] = useState<boolean>(false);
    const [contactDate, setContactDate] = useState<string>('');
    const [contactMethod, setContactMethod] = useState<number>(-1);
    const [isCoachOptIn, setIsCoachOptIn] = useState<boolean>(false);
    const [deactivateMicrosite, setDeactivateMicrosite] = useState<boolean>(undefined);


    const cancel = () => {
        reset(true);
        onClosed();
    }

    const reset = (hard) => {
        if (hard == true) {
            setEventType(-1);
            setReason(isBulk ? ReasonType.Other : -1);
            setComment('');
        }

        setTransplantDate('');
        setNkrRecipientId(undefined)
        setDashRegistrationId(undefined);
        setIsNkrTansplant(true);
        setHasMedicare(undefined);
        setDonorOriginType(-1);
        setDonorOriginOther('');
        setIsDonorFound(true);
        setNewCenterId(undefined);
        setNewCoordinatorId(-1);
        setIsNkrCenter(true);
        setTxpEdit(false);
        setShowConfirmation(false);
        setContactDate('');
        setContactMethod(-1);
        setIsCoachOptIn(false);
        setDeactivateMicrosite(undefined);
    }

    useEffect(() => {
        if (isBulk) {
            return;
        }

        if (missingData) {
            //get most recent living transplant event
            if (recipient.micrositeEventLogs) {
                let latestTxpEvents = recipient.micrositeEventLogs.filter(x => x.eventType === 1);

                if (latestTxpEvents.length > 0) {
                    let latestTxpEvent = latestTxpEvents.reduce((latest, current) =>
                        !latest || current.eventDate > latest.eventDate ? current : latest
                    );

                    handleTxpEventChange(latestTxpEvent.id);
                }
            }
        }
    }, [recipient]);

    const handleEventTypeChange = (type: number) => {
        // clear out data if deceased -> anything
        if (eventType == 2 || eventType == 16)
            reset(false);

        setEventType(type);
    }

    const handleTxpEventChange = (eventId: number) => {
        reset(true);

        //get transplant event
        let event = recipient.micrositeEventLogs.find(x => x.id == eventId);

        setEventId(event.id);
        setReason(ReasonType.Transplanted);
        setEventType(event.eventType);

        //stage event for edit
        if (recipient && event.eventDate && transplantEventTypes.includes(event.eventType)) {
            setTransplantDate(moment(event.eventDate)?.format('YYYY-MM-DD'));
        }

        if (recipient && recipient.nkrRecipientId) {
            setNkrRecipientId(recipient.nkrRecipientId);
        }

        if (recipient && recipient.dashRegistrationId) {
            setDashRegistrationId(recipient.dashRegistrationId);
        }

        if (recipient && recipient.transplantDate && recipient.nkrRecipientId < 1) {
            setHasMedicare(recipient.transplantHasMedicare);
            setIsNkrTansplant(false);
            setDonorOriginType(recipient.donorOriginId);
            setDonorOriginOther(recipient.donorOriginOther);
        }

        if (recipient && recipient.transplantDate && recipient.dashRegistrationId < 1) {
            setIsDonorFound(false);
        }

        setTxpEdit(true);
        setEventId(eventId);
    }

    const handleDateChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        setTransplantDate(e.currentTarget.value);
    }

    const handleIsNkrTransplantChange = () => {
        setIsNkrTansplant(!isNkrTansplant);

        if (isNkrTansplant) {
            setNkrRecipientId(undefined);
        } else {
            setHasMedicare(undefined);
            setDonorOriginType(-1);
            setDonorOriginOther('');
        }
    };

    const handleMedicareChange = (e: any) => {
        setHasMedicare(e.target.value == 1);
    }

    const handleOriginTypeChange = (e: any) => {
        setDonorOriginType(e.target.value);

        if (e.target.value != DonorOrigin.Other) {
            setDonorOriginOther('');
        }
    }

    const handleDonorOriginOtherChange = (e: any) => {
        setDonorOriginOther(e.target.value);
    }

    const handleIsDonorFoundChange = () => {
        setIsDonorFound(!isDonorFound);

        if (isDonorFound) {
            setDashRegistrationId(undefined);
        }
    };

    const handleCommentChange = (e: any) => {
        setComment(e.target.value);
    }

    const handleIsNkrCenterChange = () => {
        setIsNkrCenter(!isNkrCenter);

        if (isNkrCenter) {
            setNewCenterId(undefined);
            setNewCoordinatorId(-1);
        } else {
            setComment('');
        }
    }

    const handleCenterChange = (centerId: number) => {
        setNewCenterId(centerId);
        setNewCoordinatorId(-1);
    }

    const handleCoordinatorChange = (e: any) => {
        setNewCoordinatorId(e.target.value)
    };

    const transplantDateUsed = recipient?.micrositeEventLogs?.some((l) => {
        return l.id != eventId
            && transplantEventTypes.some(t => t == l.eventType)
            && moment(l.eventDate).format('YYYY-MM-DD') == transplantDate
    }) ?? false;

    const validator = {
        eventTypeValid: eventType >= 0,
        transplantDateValid: !transplantEventTypes.some(v => v == eventType)
            || (moment(transplantDate).isValid() && !transplantDateUsed),
        nkrRecipientIdValid: eventType != MicrositeEventType.ReceivedLivingKidney
            || nkrRecipientId > 0 || !isNkrTansplant,
        hasMedicareValid: eventType != MicrositeEventType.ReceivedLivingKidney || isNkrTansplant
            || hasMedicare === true || hasMedicare === false,
        donorOriginTypeValid: eventType != MicrositeEventType.ReceivedLivingKidney || isNkrTansplant
            || donorOriginType > 0,
        donorOriginOtherValid: eventType != MicrositeEventType.ReceivedLivingKidney || isNkrTansplant
            || donorOriginType != DonorOrigin.Other || donorOriginOther?.trim().length > 0,
        dashRegistrationIdValid: eventType != MicrositeEventType.ReceivedLivingKidney
            || dashRegistrationId > 0 || !isDonorFound,
        contactDateValid: ![
            MicrositeEventType.ContactAttempted,
            MicrositeEventType.ContactMade
        ].some(v => v == eventType) || moment(contactDate).isValid(),
        contactMethodValid: ![
            MicrositeEventType.ContactAttempted,
            MicrositeEventType.ContactMade
        ].some(v => v == eventType) || contactMethod > 0,
        commentValid: !(reason == ReasonType.Reactivate
            || [MicrositeEventType.NotResponsive, MicrositeEventType.OtherDeactivate, MicrositeEventType.Other, MicrositeEventType.ReadyForTransplant].some(v => v == eventType))
            || comment?.trim().length > 0,
        newCenterIdValid: eventType != MicrositeEventType.MovedNewCenter || (newCenterId > 0 && newCenterId != recipient.centerId) || !isNkrCenter,
        newCoordinatorIdValid: eventType != MicrositeEventType.MovedNewCenter || newCoordinatorId > 0 || !isNkrCenter,
        newCenterOtherValid: eventType != MicrositeEventType.MovedNewCenter || isNkrCenter || comment?.trim().length > 0,
        deactivateMicrositeValid: eventType != MicrositeEventType.TooSick || deactivateMicrosite === true || deactivateMicrosite === false
    };

    const isValid = Object.entries(validator).every(([_k, v]) => v === true);

    const handleChangeStatusClicked = () => {
        if (isValid) {
            //bulk always confirms, regardless of event type
            if (isBulk || confirmationTypes.some(t => t == eventType)) {
                setShowConfirmation(true);
            } else {
                raiseStatusChanged();
            }
        }
    };

    const handleConfirmation = () => {
        setShowConfirmation(false);
        raiseStatusChanged();
    };

    const raiseStatusChanged = () => {
        const request: IChangeStatusRequest = {
            eventId,
            eventType,
            transplantDate,
            isNkrTansplant,
            nkrRecipientId,
            hasMedicare,
            donorOriginType,
            donorOriginOther,
            isDonorFound,
            dashRegistrationId,
            comment,
            newCenterId,
            newCoordinatorId,
            isNkrCenter,
            contactDate,
            contactMethod,
            isCoachOptIn,
            deactivateMicrosite: eventType == MicrositeEventType.TooSick ? deactivateMicrosite : undefined
        };

        if (isBulk) {
            onBulkStatusChanged(request);
        } else {
            onStatusChanged(recipient, request);
        }

        reset(true);
        onClosed();
    };

    const setRecipientId = (value: number) => {
        setIsNkrTansplant(true);
        setNkrRecipientId(value);
    };


    let currStatus = recipient?.currentStatusId ?? 0;
    return (
        <>
            {isBulk
                ? <BulkStatusConfirmation
                    show={showConfirmation}
                    onConfirm={handleConfirmation}
                    onCancel={() => { setShowConfirmation(false); }}
                    eventType={eventType}
                    patientCount={recipients?.length ?? 0}
                    eventTypeList={eventTypeList}
                />
                : <StatusConfirmation
                    show={showConfirmation}
                    onConfirm={handleConfirmation}
                    onCancel={() => { setShowConfirmation(false); }}
                    eventType={eventType}
                    eventTypeList={eventTypeList}
                    deactivateMicrosite={eventType == MicrositeEventType.TooSick && deactivateMicrosite === true}
                />
            }

            <Drawer
                width="45vw"
                placement="right"
                open={open}
            >
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div style={{ padding: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <h3 style={{ margin: 0 }}>Change Microsite Patient Status</h3>
                            <Button bsStyle='link' bsSize='sm' onClick={() => cancel()}><Glyphicon glyph='remove' /></Button>
                        </div>

                        <ViewWrapper>
                            {isBulk && <h4>Multiple Patients ({recipients?.length ?? 0} selected)</h4>}
                            {!isBulk && recipient && recipient.center && <h4>{recipient.fullName} @ {recipient.center.alias} (Id: {recipient.id})</h4>}
                            {!isBulk && recipient && !recipient.center && <h4>{recipient.fullName} (Id: {recipient.id})</h4>}
                        </ViewWrapper>

                        <hr />

                        {isTxpEdit &&
                            <Row>
                                <Alert bsStyle='warning'>Updating Existing Transplant Record</Alert>
                            </Row>
                        }

                        <EventTypePicker
                            eventType={eventType}
                            reason={reason}
                            isEditMode={isTxpEdit}
                            isBulk={isBulk}
                            currentStatus={currStatus}
                            contactDate={contactDate}
                            contactMethod={contactMethod}
                            isCoachOptIn={isCoachOptIn}
                            onEventTypeChange={handleEventTypeChange}
                            onReasonChange={setReason}
                            onContactDateChange={setContactDate}
                            onContactMethodChange={setContactMethod}
                            onCoachOptInChange={setIsCoachOptIn}
                        />

                        {eventType == MicrositeEventType.TooSick &&
                            <Row>
                                <Col sm={12} md={12}>
                                    <FormGroup>
                                        <ControlLabel>Should This Microsite Be Deactivated? <Req /></ControlLabel>
                                        <FormControl
                                            componentClass='select'
                                            name='deactivateMicrosite'
                                            value={deactivateMicrosite === true ? 1 : deactivateMicrosite === false ? 0 : -1}
                                            onChange={(e: any) => setDeactivateMicrosite(Number(e.target.value) == 1)}>
                                            <option value={-1} disabled>---</option>
                                            <option value={1}>Yes</option>
                                            <option value={0}>No</option>
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                            </Row>
                        }

                        {transplantEventTypes.some(v => v == eventType) &&
                            <Row>
                                <Col sm={12} md={12}>
                                    <FormGroup>
                                        <ControlLabel>Transplant Date <Req /></ControlLabel>
                                        <FormControl
                                            type='date'
                                            name='transplantDate'
                                            value={transplantDate}
                                            onChange={handleDateChange}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        }

                        {eventType == MicrositeEventType.ReceivedLivingKidney &&
                            <>
                                <Row>
                                    <Col sm={12} md={12}>
                                        <FormGroup>
                                            <ControlLabel>Patient's NKR Alias <Req /></ControlLabel>
                                            <HelpBlock>Search for Patient by <strong>Name</strong>, <strong>Alias</strong>, <strong>Birth Date</strong>, or <strong>Transplant Date</strong>.</HelpBlock>
                                            <NkrRecipientTypeahead
                                                onChange={(value) => setRecipientId(value)}
                                                value={nkrRecipientId}
                                                micrositeId={recipient?.id}
                                                enabled={isNkrTansplant}
                                            />
                                            <Checkbox checked={!isNkrTansplant} onClick={handleIsNkrTransplantChange}>Not an NKR Transplant</Checkbox>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {!isNkrTansplant &&
                                    <>
                                        <Row>
                                            <Col sm={12} md={12}>
                                                <FormGroup>
                                                    <Col>
                                                        <ControlLabel>Patient Has Medicare? <Req /></ControlLabel>
                                                    </Col>
                                                    <Col>
                                                        <Radio inline name='hasMedicare' id='hasMedicareYes' value={1} checked={hasMedicare == true} onChange={handleMedicareChange}>Yes</Radio>
                                                        <Radio inline name='hasMedicare' id='hasMedicareNo' value={0} checked={hasMedicare == false} onChange={handleMedicareChange}>No</Radio>
                                                    </Col>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm={12} md={12}>
                                                <FormGroup>
                                                    <ControlLabel>How was the patient transplanted? <Req /></ControlLabel>
                                                    <FormControl
                                                        componentClass='select'
                                                        name='donorOriginType'
                                                        value={donorOriginType}
                                                        onChange={handleOriginTypeChange}
                                                    >
                                                        <option value={-1} disabled>- Please Select -</option>
                                                        <option value={DonorOrigin.Direct}>Internal Direct</option>
                                                        <option value={DonorOrigin.Other}>Other</option>
                                                    </FormControl>
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        {donorOriginType == DonorOrigin.Other &&
                                            <Row>
                                                <Col sm={12} md={12}>
                                                    <FormGroup>
                                                        <ControlLabel>Other (please explain) <Req /></ControlLabel>
                                                        <FormControl
                                                            name='donorOriginOther'
                                                            value={donorOriginOther}
                                                            onChange={handleDonorOriginOtherChange}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        }
                                    </>
                                }
                            </>
                        }

                        {eventType == MicrositeEventType.ReceivedLivingKidney &&
                            <Row>
                                <Col sm={12} md={12}>
                                    <FormGroup>
                                        <ControlLabel>DASH Donor <Req /></ControlLabel>
                                        <HelpBlock>Search for DASH donor by <strong>Name</strong>, <strong>Birth Date</strong>, or <strong>Email</strong>.</HelpBlock>
                                        <DashRegistrationTypeahead
                                            micrositeId={recipient?.id}
                                            recipientId={recipient.nkrRecipientId}
                                            onChange={setDashRegistrationId}
                                            value={dashRegistrationId}
                                            enabled={isDonorFound}
                                        />
                                        <Checkbox checked={!isDonorFound} onClick={handleIsDonorFoundChange}>Donor Not Found in DASH</Checkbox>
                                    </FormGroup>
                                </Col>
                            </Row>
                        }

                        {(reason == ReasonType.Reactivate ||
                            [MicrositeEventType.NotResponsive, MicrositeEventType.OtherDeactivate, MicrositeEventType.Other, MicrositeEventType.ReadyForTransplant].some(v => v == eventType)) &&
                            <Row>
                                <Col sm={12} md={12}>
                                    <FormGroup>
                                        <ControlLabel>Why? (please explain) <Req /></ControlLabel>
                                        <FormControl
                                            name='comment'
                                            value={comment}
                                            onChange={handleCommentChange}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        }

                        {eventType == MicrositeEventType.MovedNewCenter &&
                            <>
                                <Row>
                                    <Col sm={12} md={12}>
                                        <FormGroup>
                                            <ControlLabel>From Center</ControlLabel>
                                            <CenterTypeahead value={recipient?.centerId} enabled={false} />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm={12} md={12}>
                                        <FormGroup>
                                            <ControlLabel>To Center <Req /></ControlLabel>
                                            <HelpBlock>Search for Center by <strong>Alias</strong>, <strong>Address</strong>, <strong>City</strong>, or <strong>State</strong> (abbreviation).</HelpBlock>
                                            <CenterTypeahead value={newCenterId} onChange={handleCenterChange} enabled={isNkrCenter} onlyMicrositeCenters={true} />
                                            <Checkbox checked={!isNkrCenter} onClick={handleIsNkrCenterChange}>Not an NKR center</Checkbox>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {isNkrCenter &&
                                    <Row>
                                        <Col sm={12} md={12}>
                                            <CoordinatorPicker
                                                value={newCoordinatorId}
                                                onChange={handleCoordinatorChange}
                                                centerId={newCenterId}
                                                coordinators={coordinatorList}
                                            />
                                        </Col>
                                    </Row>
                                }

                                {!isNkrCenter &&
                                    <Row>
                                        <Col sm={12} md={12}>
                                            <FormGroup>
                                                <ControlLabel>Non-NKR Center <Req /></ControlLabel>
                                                <FormControl
                                                    name='centerOther'
                                                    value={comment}
                                                    onChange={handleCommentChange}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                }
                            </>
                        }

                        <Row>
                            <Col sm={12} md={12}>
                                <Button disabled={!isValid} bsStyle="primary" block onClick={handleChangeStatusClicked}>
                                    Change Status
                                </Button>
                            </Col>
                        </Row>

                        {!isBulk &&
                            <>
                                <hr />
                                <Row>
                                    <Col sm={12} md={12}>
                                        <EventHistoryAccordion
                                            recipient={recipient}
                                            eventTypeList={eventTypeList}
                                            onTxpEdit={handleTxpEventChange}
                                        />
                                    </Col>
                                </Row>
                            </>
                        }
                    </div>

                    <div className='text-center bg-info'>
                        <Button bsStyle='link' block onClick={() => cancel()}>Cancel</Button>
                    </div>
                </div>
            </Drawer>
        </>
    );
}